// Client-safe data module — pure functions only, no fetches and no bundled data.
// The chat's scope is always the merchant the route is on, so there is no brand
// catalogue here; `brandFromMerchant` turns that merchant into display fields.

export type Tone = "brand" | "pink" | "blue" | "success" | "warning" | "error" | "gray";

export type Brand = {
  id: string;
  name: string;
  displayName: string;
  country: string | null;
  initials: string;
  tone: Tone;
  category: string;
  kind: "brand";
};

export type Scope = Brand;

export type PromptGroup = {
  id: string;
  label: string;
  icon: string;
  accent: string;
  accentBg: string;
  prompts: string[];
};

export const PROMPT_GROUPS: PromptGroup[] = [
  {
    id: "performance",
    label: "Performance",
    icon: "trending",
    accent: "var(--color-brand-600)",
    accentBg: "var(--color-brand-50)",
    prompts: [
      "What were {scope}'s top 5 videos by GMV last 30 days?",
      "Show GMV trend for {scope} over the past 90 days.",
      "Which creators drove the most revenue for {scope}?",
      "How does {scope}'s conversion rate compare to the category benchmark?",
    ],
  },
  {
    id: "safety",
    label: "Safety",
    icon: "shield",
    accent: "var(--color-error-600)",
    accentBg: "var(--color-error-50)",
    prompts: [
      "What unresolved risks does {scope} have right now?",
      "Show videos flagged for {scope} this week.",
      "Which {scope} creators are above the risk threshold?",
      "Trend of safety incidents for {scope} over the last 60 days.",
    ],
  },
  {
    id: "creators",
    label: "Creators",
    icon: "users",
    accent: "var(--color-success-600)",
    accentBg: "var(--color-success-50)",
    prompts: [
      "Top dormant creators for {scope} worth re-engaging.",
      "How many active creators does {scope} have this month?",
      "Which creators recently churned from {scope}?",
      "Show top performers for {scope} by GMV per video.",
    ],
  },
  {
    id: "videos",
    label: "Videos",
    icon: "play",
    accent: "var(--color-warning-600)",
    accentBg: "var(--color-warning-50)",
    prompts: [
      "Top performing videos for {scope} this week.",
      "Videos with declining views for {scope}.",
      "Latest published videos for {scope}.",
      "What is the average GPM for {scope} videos this month?",
    ],
  },
];

export function expandPrompt(template: string, scope: Scope | null): string {
  const name = scope ? scope.displayName || scope.name : "your portfolio";
  return template.replaceAll("{scope}", name);
}

/**
 * Return `n` items chosen pseudo-randomly from `arr` without replacement.
 * `rng` defaults to Math.random and is injectable for deterministic tests.
 * Does not mutate `arr`.
 */
export function pickN<T>(arr: readonly T[], n: number, rng: () => number = Math.random): T[] {
  const pool = [...arr];
  const out: T[] = [];
  const count = Math.min(n, pool.length);
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(rng() * pool.length) % pool.length;
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Merchant -> Brand display fields
// ---------------------------------------------------------------------------

/** Strip a trailing region suffix like " (US)" — merchant names already carry it. */
function stripRegion(name: string): string {
  return name.replace(/\s*\(([A-Z]{2,3})\)\s*$/, "").trim();
}

function makeInitials(name: string): string {
  const stripped = stripRegion(name);
  if (/^[A-Z]{2,}/.test(stripped) && stripped.length <= 4) return stripped.slice(0, 2);
  const parts = stripped
    .replace(/&/g, " ")
    .split(/[\s.]+/)
    .filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return stripped.slice(0, 2).toUpperCase();
}

function hashTone(id: string): Tone {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const pool: Tone[] = ["brand", "pink", "blue", "success", "warning", "gray"];
  return pool[h % pool.length];
}

export function brandFromMerchant(merchant: { id: string; name: string; vertical?: string | null }): Brand {
  return {
    id: merchant.id,
    name: merchant.name,
    displayName: stripRegion(merchant.name),
    country: null,
    initials: makeInitials(merchant.name),
    tone: hashTone(merchant.id),
    category: merchant.vertical ?? "",
    kind: "brand",
  };
}
